import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../reduxStore/Store';
import {
  fetchReglementData, deleteReglement, setSelectedReglement, clearReglementData,
} from '../reduxStore/ReglementSlice';
import MyTable from '../components/MyTable';
import { ButtonType } from '../constants/enums';
import { TableHeader, TableAction } from '../constants/interfaces';
import { Reglement } from '../models/models';
import OperationDiverseForm from '../components/Forms/OperationDiverseForm';
import TransfertForm from '../components/Forms/TransfertForm';
import { DialogTitle, Typography } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { useDialog } from '../components/DialogComponent';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import AppOutlineButton from '../components/layout/AppOutlineButton';
import { getLabels } from '../constants/fieldLabels';
import { formatStringDate } from '../utils/utils';
import { useSnackbar } from '../components/SnackbarProvider';
import OperationDiverseFilters, { ODFilterValues, OD_EMPTY_FILTERS } from '../components/Filters/OperationDiverseFilters';

/**
 * Opérations diverses : écritures sur les comptes tiers (client / fournisseur /
 * employé) SANS impact caisse. Stockées comme des règlements de type
 * OPERATION_DIVERSE (cf. ReglementSlice / migration 0079).
 */
const GestionOperationDiverse: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { reglementData, total, loading, error, successMessage, selectedReglement } =
    useSelector((state: any) => state.ReglementSlice);
  const { isAllMode } = useSelector((state: any) => state.AppSlice);
  const { showSnackbar } = useSnackbar();

  const [fetchParams, setFetchParams] = useState({ limit: 5, offset: 0 });
  const [filters, setFilters] = useState<ODFilterValues>(OD_EMPTY_FILTERS);

  const [FormDialog, openDialog, closeDialog] = useDialog({
    dialogProps: { maxWidth: 'sm', fullWidth: true },
  });
  const [TransfertDialog, openTransfertDialog, closeTransfertDialog] = useDialog({
    dialogProps: { maxWidth: 'sm', fullWidth: true },
  });

  useEffect(() => {
    dispatch(fetchReglementData({
      owner_id: 0,                     // placeholder — écrasé par l'intercepteur axios
      limit: fetchParams.limit,
      offset: fetchParams.offset,
      type_reglement: 'OPERATION_DIVERSE',
      sens: filters.sens,
      tiers_id: filters.tiers ? filters.tiers.id : undefined,
      date_start: filters.date_start || undefined,
      date_end: filters.date_end || undefined,
      montant: filters.montant !== '' ? Number(filters.montant) : null,
      departement_id: filters.departement ? filters.departement.id : undefined,
    }));
    return () => {
      dispatch(setSelectedReglement(null));
      dispatch(clearReglementData());
    };
  }, [dispatch, fetchParams, filters, successMessage]);

  // La recherche ne s'applique qu'au clic (le brouillon vit dans le composant filtre).
  const handleSearch = (next: ODFilterValues) => {
    setFilters(next);
    setFetchParams(prev => ({ ...prev, offset: 0 })); // nouvelle recherche → page 1
  };

  const handleFiltersReset = () => {
    setFilters(OD_EMPTY_FILTERS);
    setFetchParams(prev => ({ ...prev, offset: 0 }));
  };

  const handleAdd = () => {
    dispatch(setSelectedReglement(null));
    openDialog();
  };

  const handleCancel = () => {
    closeDialog();
    dispatch(setSelectedReglement(null));
  };

  const headers: TableHeader<Reglement>[] = [
    { key: 'date_reglement',   label: 'Date',    noTruncate: true, width: 100, render: (r) => formatStringDate(r.date_reglement) },
    // Visible seulement en mode "Tous départements" : dans un contexte de département
    // actif, toutes les lignes appartiennent déjà au même département (redondant).
    ...(isAllMode ? [{
      key: 'departement_nom' as const,
      label: 'Département',
      width: 130,
      render: (r: Reglement) => r.departement_nom ?? '—',
    }] : []),
    { key: 'numero_reglement', label: 'Numéro',  noTruncate: true, width: 110 },
    {
      key: 'tiers', label: 'Tiers',
      render: (r) => {
        const nom = r.tiers?.nom;
        const prenom = r.tiers?.prenom;
        return (!nom && !prenom) ? 'N/A' : `${nom || ''} ${prenom || ''}`.trim();
      },
    },
    { key: 'sens',     label: 'Sens',    width: 80, render: (r) => (r.sens === 'C' ? 'Crédit' : 'Débit') },
    { key: 'montant',  label: 'Montant', align: 'right', noTruncate: true, width: 110 },
    { key: 'reference', label: 'Libellé', width: 200 },
  ];

  const tableActions: TableAction<Reglement>[] = [
    {
      label: 'Edit',
      type: ButtonType.EDIT,
      callback: (reglement) => {
        dispatch(setSelectedReglement(reglement));
        openDialog();
      },
    },
    {
      label: 'Delete',
      type: ButtonType.DELETE,
      callback: async (reglement) => {
        if (reglement && window.confirm('Voulez-vous vraiment supprimer cette opération diverse ?')) {
          try {
            await dispatch(deleteReglement(reglement.id)).unwrap();
            showSnackbar('Opération supprimée avec succès !', 'success');
          } catch (e) {
            showSnackbar((e as string) || 'Erreur lors de la suppression', 'error');
          }
        }
      },
    },
  ];

  const handlePageChange = (newPage: number, newRowsPerPage: number) => {
    setFetchParams({ offset: newPage * newRowsPerPage, limit: newRowsPerPage });
  };

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title="Opérations diverses"
        subtitle={`${total} opération${total > 1 ? 's' : ''} enregistrée${total > 1 ? 's' : ''}`}
        icon={<SwapHorizIcon />}
        addLabel="Nouvelle opération"
        onAdd={handleAdd}
        actions={
          <AppOutlineButton startIcon={<SyncAltIcon />} onClick={openTransfertDialog}>
            Transfert
          </AppOutlineButton>
        }
      />

      {loading && <Typography color="#667085" mb={2}>Chargement...</Typography>}
      {error && <Typography color="#b42318" mb={2}>Erreur : {error}</Typography>}

      <AppSurface sx={{ mb: 2 }} padding={0}>
        <OperationDiverseFilters
          onSearch={handleSearch}
          onReset={handleFiltersReset}
        />
      </AppSurface>

      <AppSurface>
        <MyTable
          headers={headers}
          data={reglementData}
          onPageChange={handlePageChange}
          actions={tableActions}
          totalRows={total}
          showDetails
          fieldLabels={getLabels('Reglement')}
          tableKey="operations_diverses"
        />
      </AppSurface>

      <FormDialog>
        <DialogTitle>
          <Typography variant="h6">
            {selectedReglement ? 'Modifier une opération diverse' : 'Nouvelle opération diverse'}
          </Typography>
        </DialogTitle>
        <OperationDiverseForm onCancel={handleCancel} />
      </FormDialog>

      <TransfertDialog>
        <DialogTitle>
          <Typography variant="h6">Transfert entre deux comptes</Typography>
        </DialogTitle>
        <TransfertForm onCancel={closeTransfertDialog} />
      </TransfertDialog>
    </AppPage>
  );
};

export default GestionOperationDiverse;
